import { useCallback, useState } from "react";
import { useTranslation } from "react-i18next";
import { useMutation } from "@tanstack/react-query";
import moment from "moment-timezone";
import { omit } from "lodash";
import { toast } from "sonner";
import Dialog from "../../../../../../components/shared/Dialog";
import Button from "../../../../../../components/shared/Button";
import UsersDisplay from "./UsersDisplay";
import SuccessTable from "./SuccessTable";
import { useScheduleContext } from "../../../hooks/context/useScheduleContext";
import { useFilters } from "../../../../../../utils/hooks/useFilters";
import useApi from "../../../../../../utils/api";
import { calculateShiftsToAssign } from "../../../helpers/modals/calculateShiftsToAssign";
import { getFormFormattedValues } from "../../../helpers/form/getFormFormattedValues";
import { getErrorMessage } from "../../../../../../utils/helpers/errors";
import { withLoadingToast } from "../../../helpers/withLoadingToast";

function Assign({ visible, close, values }: any) {
    const { t } = useTranslation();
    const { authPut } = useApi();
    const [data, setData] = useState([]);
    const [isSubmitted, setIsSubmitted] = useState(false);
    const [selectedUsers, setSelectedUsers] = useState([]);
    const [filters] = useFilters();
    const { dispatch } = useScheduleContext();

    const assignMutation = useMutation({
        ...withLoadingToast(t("assigning")),
        mutationFn: (data: any) => {
            return authPut(`/unassigned-shifts/${values?.id}/assign`, { data });
        },
        onSuccess: (response: any) => {
            setData(response);
            setIsSubmitted(true);
        },
        onError: (err) => getErrorMessage(err, t),
    });

    const submit = useCallback(() => {
        if (selectedUsers?.length === 0) {
            toast.error(t("required-users"));
            return;
        }
        const formattedData = selectedUsers?.map((user: any) => {
            const calculatedValues = calculateShiftsToAssign(values, user);
            const formattedValues = getFormFormattedValues({
                ...values,
                ...calculatedValues,
            });
            return omit(
                { ...formattedValues, user: user?.id },
                ["resourceId", "isUnassignedShift", "updatedAt", "createdAt", "slots", "_id", "__v", "id", "type"]
            );
        });
        assignMutation.mutate(formattedData);
    }, [selectedUsers, assignMutation, t, values]);

    const cancel = useCallback(() => {
        const success = data?.filter((item: any) => item?.status) ?? [];
        const slots = Number(values?.slots) - success.length;

        success.forEach((item: any) => {
            const timezone = moment.tz.guess();
            const startDate = filters?.mode === "month"
                ? moment(item.shift.startDate).tz(timezone, true)
                : moment.parseZone(item.shift.startDate).tz(timezone, true);
            const endDate = filters?.mode === "month"
                ? moment(item.shift.startDate).tz(timezone, true)
                : moment.parseZone(item.shift.endDate).tz(timezone, true);
            const type = filters?.mode === "month" ? "create-calendar-event" : "create-event";

            dispatch({
                type,
                payload: {
                    ...item.shift,
                    startDate,
                    endDate,
                    resourceId: item?.user?.id || item?.user
                },
            });
        });

        if (slots > 0) {
            dispatch({
                type: "update-unassigned-event",
                payload: { ...values, slots },
            });
        } else {
            dispatch({ type: "delete-unassigned-event", payload: { id: values?.id } });
        }

        setSelectedUsers([]);
        setData([]);
        setIsSubmitted(false);
        close();
    }, [close, data, values, filters?.mode, dispatch]);

    return (
        <Dialog
            open={visible}
            close={cancel}
            title={t("assign")}
            size="2xl"
            bodyClassName="tw-max-h-[300px] tw-overflow-y-auto tw-p-1 tw--m-1"
            footer={(
                <div className="tw-w-full tw-flex tw-items-center tw-gap-2 tw-justify-end">
                    <Button
                        variant="ghost"
                        type="button"
                        onClick={cancel}
                        disabled={assignMutation.isPending}
                        loading={assignMutation.isPending}
                    >
                        {t("cancel")}
                    </Button>
                    {!isSubmitted && (
                        <Button
                            type="button"
                            onClick={submit}
                            disabled={assignMutation.isPending}
                            loading={assignMutation.isPending}
                        >
                            {t("assign")}
                        </Button>
                    )}
                </div>
            )}
        >
            {isSubmitted ? (
                <SuccessTable
                    data={data}
                    selectedUsers={selectedUsers}
                />
            ) : (
                <UsersDisplay
                    shift={values}
                    selectedUsers={selectedUsers}
                    setSelectedUsers={setSelectedUsers}
                />
            )}
        </Dialog>
    );
}

export default Assign;
